import React, { useState, useEffect } from 'react';
import './StoryPrompt.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { StoryFormData } from '../../types/StoryFormData';
import ProgressBar from '../../components/ProgressBar/ProgressBar';

const StoryPrompt: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const formData = location.state as StoryFormData;
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Initialize formData with default story_preferences if it doesn't exist
  const initialFormData = {
    ...formData,
    story_preferences: formData.story_preferences || {
      is_public: true,
      longer_text: false,
      rhyming_story: false,
      page_count: 4
    }
  };
  
  const [localFormData, setLocalFormData] = useState<StoryFormData>(initialFormData);

  const handleNext = () => {
    navigate('/create/3-review', { state: localFormData });
  };

  const handleCheckboxChange = (field: keyof typeof localFormData.story_preferences) => {
    setLocalFormData(prev => {
      return {
        ...prev,
        story_preferences: {
          ...prev.story_preferences,
          [field]: !prev.story_preferences[field]
        }
      };
    });
  };

  const handlePageCountChange = (value: number) => {
    setLocalFormData(prev => {
      return {
        ...prev,
        story_preferences: {
          ...prev.story_preferences,
          page_count: value
        }
      };
    });
  };

  return (
    <div className="story-prompt">
      {/* Hero title section with background */}
      <div className="title-hero-wrapper">
        <div className="title-container">
          <div className="self-stretch text-center justify-start text-fuchsia-500 text-3xl font-normal font-['Combo'] tracking-wide">Create a Book</div>
          <div className="self-stretch text-center justify-start text-black text-5xl font-medium font-['Baloo_2'] leading-[66px]">Create your personalized ai storybook!</div>
        </div>
      </div>
      
      {/* Progress Bar */}
      <ProgressBar currentStep={2} />

      <div className="story-prompt-container">
        <div className="back-navigation-section">
          <div className="back-nav-bg"></div>
          <div className="back-nav-content">
            <button className="back-button" onClick={() => navigate('/create/1-write', { state: localFormData })}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 19L5 12L12 5" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 12H5" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="review-page-title">Story Preferences</div>
          </div>
        </div>

        <div className="container-content">
          <div className="story-details">
            <div className="detail-section">
              <h3>Visibility</h3>
              <div className="toggle-option">
                <div className="toggle-row">
                  <input
                    type="checkbox"
                    id="public"
                    checked={localFormData.story_preferences.is_public}
                    onChange={() => handleCheckboxChange('is_public')}
                  />
                  <label htmlFor="public">Public</label>
                </div>
                <p className="option-description">
                  You can choose to have your book public, so other people can see it and read it.
                </p>
              </div>
            </div>

            <div className="detail-section">
              <h3>Longer Text</h3>
              <div className="toggle-option">
                <div className="toggle-row">
                  <input
                    type="checkbox"
                    id="longerText"
                    checked={localFormData.story_preferences.longer_text}
                    onChange={() => handleCheckboxChange('longer_text')}
                  />
                  <label htmlFor="longerText">Longer text</label>
                </div>
                <p className="option-description">
                  You can choose to have a longer text, so the story pages will be generated with more content.
                </p>
              </div>
            </div>

            <div className="detail-section">
              <h3>Rhyming Story</h3>
              <div className="toggle-option">
                <div className="toggle-row">
                  <input
                    type="checkbox"
                    id="rhymingStory"
                    checked={localFormData.story_preferences.rhyming_story}
                    onChange={() => handleCheckboxChange('rhyming_story')}
                  />
                  <label htmlFor="rhymingStory">Rhyming story</label>
                </div>
                <p className="option-description">
                  You can choose to have a rhyming story, so the story will be generated with rhymes.
                </p>
              </div>
            </div>

            <div className="detail-section">
              <h3>Page Count</h3>
              <div className="setting-group">
                <select 
                  value={localFormData.story_preferences.page_count} 
                  onChange={(e) => handlePageCountChange(Number(e.target.value))}
                >
                  <option value={4}>4</option>
                  <option value={6}>6</option>
                  <option value={8}>8</option>
                  {/* Add other page count options */}
                </select>
              </div>
            </div>
          </div>

          <div className="button-container">
            <button className="next-btn" onClick={handleNext}>
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryPrompt; 